import React, { useState, useEffect } from "react";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap/dist/css/bootstrap.min.css";
import ConfirmModal from "../../Components/Modals/ConfirmModal";
import UserCommentCard from "./UserCommentCard";
import { CheckRole } from "../../Services/AuthService";
import { MessageSquare } from "lucide-react";
import Notification from "../../Components/Core/Notification";
import {
  getUnCheckComment,
  deleteComment,
  checkComment,
  replyComment,
} from "../../Services/CommentService";
import Loader from "../../Components/Core/Loader";
import ReviewModeration from "../../Components/Reviews/ReviewModeration";

const UnCheckComments = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openReplyModal, setOpenReplyModal] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [isReplying, setIsReplying] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getUnCheckComment();
      setComments(response);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "بررسی نظرات کاربران";
    fetchData();
  }, []);

  const handleCheck = async (id) => {
    try {
      await checkComment(id);
      setComments(comments.filter((comment) => comment.id !== id));
      setSnackbarMessage("دیدگاه با موفقیت چک شد");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      console.log(error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      setIsDeleting(true);
      await deleteComment(id);
      setComments(comments.filter((comment) => comment.id !== id));
      setSnackbarMessage("دیدگاه با موفقیت حذف شد");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      console.log(error.message);
      setSnackbarMessage("خطا در حذف دیدگاه");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDelete = (id) => {
    setSelectedId(id);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedId) {
      await handleDelete(selectedId);
      setSelectedId(null);
    }
    setOpenDialog(false);
  };

  const handleReply = async (commentId) => {
    setSelectedCommentId(commentId);
    setReplyText("");
    setOpenReplyModal(true);
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim() || !selectedCommentId) {
      setSnackbarMessage("لطفا متن پاسخ را وارد کنید");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    try {
      setIsReplying(true);
      const comment = comments.find((c) => c.id === selectedCommentId);

      const replyData = {
        BlogId: comment.blogId,
        Content: replyText,
      };

      await replyComment(replyData, selectedCommentId);

      setSnackbarMessage("پاسخ شما با موفقیت ثبت شد");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      setOpenReplyModal(false);
      setReplyText("");

      await fetchData();

      const response = await getUnCheckComment();
      const updatedParentComment = response.find(
        (c) => c.id === selectedCommentId
      );

      if (updatedParentComment && updatedParentComment.replies?.length > 0) {
        const newReply =
          updatedParentComment.replies[updatedParentComment.replies.length - 1];

        await handleCheck(newReply.id);
        await handleCheck(selectedCommentId);
      } else {
        // If no replies found, just check the parent comment
        await handleCheck(selectedCommentId);
      }

      setSelectedCommentId(null);
    } catch (error) {
      console.log(error.message);
      setSnackbarMessage("خطا در ارسال پاسخ");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setIsReplying(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("fa-IR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <>
      <style jsx>{`
        .page-header {
          background-color: #053251 !important;
          padding: 1.5rem 2rem !important;
          border-radius: 12px !important;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          flex-wrap: wrap !important;
          gap: 1rem !important;
        }

        .page-header-left {
          display: flex !important;
          align-items: center !important;
          gap: 1rem !important;
        }

        .page-header-icon {
          color: #7cc890 !important;
        }

        .page-header-title {
          margin: 0 !important;
          color: #fff !important;
          font-weight: 600 !important;
        }

        .page-header-badge {
          background-color: #7cc890 !important;
          font-size: 1rem !important;
          padding: 0.5rem 1rem !important;
          border-radius: 20px !important;
          font-weight: 500 !important;
          color: #053251 !important;
        }
      `}</style>

      <div
        style={{
          backgroundColor: "#f8f9fa",
          minHeight: "100vh",
          paddingTop: "2rem",
          paddingBottom: "2rem",
        }}
        className="mb-5 pb-5"
      >
        <div className="container">
          {/* Header */}
          <div className="row mb-4 iran-sans">
            <div className="col-12">
              <div className="page-header">
                <div className="page-header-left">
                  <MessageSquare className="page-header-icon" size={32} />
                  <h2 className="page-header-title">بررسی نظرات کاربران</h2>
                </div>
                <div className="page-header-badge">{comments.length.toLocaleString("fa-IR")} نظر مقاله</div>
              </div>
            </div>
          </div>

          {CheckRole(["Admin", "Doctor"]) && <ReviewModeration />}
          <h3 className="iran-sans color-blue fs-5 mb-3">نظرات مقاله‌ها</h3>
          {/* Content */}
          {loading ? (
            <Loader />
          ) : comments.length === 0 ? (
            <div
              className="text-center iran-sans"
              style={{ padding: "4rem 2rem" }}
            >
              <MessageSquare
                size={64}
                color="#053251"
                style={{ opacity: 0.3, marginBottom: "1rem" }}
              />
              <h4
                className="iran-sans"
                style={{ color: "#053251", marginBottom: "0.5rem" }}
              >
                کامنتی برای بررسی وجود ندارد
              </h4>
              <p className="iran-sans" style={{ color: "#6c757d" }}>
                همه کامنت‌ها بررسی شده‌اند
              </p>
            </div>
          ) : (
            <div className="row g-4">
              {comments.map((comment) => (
                <div key={comment.id} className="col-12 col-md-6">
                  <UserCommentCard
                    comment={comment}
                    formatDate={formatDate}
                    handleCheck={handleCheck}
                    handleReply={handleReply}
                    confirmDelete={confirmDelete}
                    isDeleting={isDeleting}
                    CheckRole={CheckRole}
                    DestinationPage={"UnCheckComments"}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          openDialog={openDialog}
          handleCloseDialog={() => setOpenDialog(false)}
          handleConfirm={handleConfirmDelete}
          text={["آیا از حذف این نظر مطمئن هستید؟"]}
          confirmText={"بله"}
          cancleText={"خیر"}
          title={"حذف نظر"}
        />

        {/* Reply Modal */}
        <div
          className={`modal fade iran-sans rounded-3 ${
            openReplyModal ? "show" : ""
          }`}
          style={{ display: openReplyModal ? "block" : "none" }}
          tabIndex="-1"
          aria-hidden={!openReplyModal}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "12px" }}>
              <div
                className="modal-header"
                style={{ backgroundColor: "#053251", color: "#fff" }}
              >
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setOpenReplyModal(false);
                    setReplyText("");
                    setSelectedCommentId(null);
                  }}
                  disabled={isReplying}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label
                    className="form-label iran-sans mb-3 fw-bold color-blue"
                    style={{ fontSize: "1.1rem" }}
                  >
                    متن پاسخ:
                  </label>
                  <textarea
                    className="form-control iran-sans"
                    rows="5"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="پاسخ خود را اینجا بنویسید..."
                    disabled={isReplying}
                    style={{
                      borderColor: "#7cc890",
                      resize: "vertical",
                    }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn iran-sans px-4"
                  style={{
                    fontSize: "0.9rem",
                    backgroundColor: "#6c757d",
                    color: "#fff",
                    border: "none",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#5d656dff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                  onClick={() => {
                    setOpenReplyModal(false);
                    setReplyText("");
                    setSelectedCommentId(null);
                  }}
                  disabled={isReplying}
                >
                  لغو
                </button>
                <button
                  type="button"
                  className="btn iran-sans px-4"
                  style={{
                    fontSize: "0.9rem",
                    backgroundColor: "#7cc890",
                    color: "#fff",
                    border: "none",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6ab57d")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#7cc890")
                  }
                  onClick={handleSubmitReply}
                  disabled={isReplying || !replyText.trim()}
                >
                  {isReplying ? "در حال ارسال..." : "ارسال پاسخ"}
                </button>
              </div>
            </div>
          </div>
        </div>
        {openReplyModal && (
          <div
            className="modal-backdrop fade show"
            onClick={() => {
              if (!isReplying) {
                setOpenReplyModal(false);
                setReplyText("");
                setSelectedCommentId(null);
              }
            }}
          />
        )}

        <Notification
          openSnackbar={openSnackbar}
          snackbarMessage={snackbarMessage}
          snackbarSeverity={snackbarSeverity}
          setOpenSnackbar={setOpenSnackbar}
        />
      </div>
    </>
  );
};

export default UnCheckComments;
